import type { StorefrontEventContent } from '@xshop/shared';
import type { StorefrontProduct } from '../api/client';
import { trackEvent, type StorefrontEventDetail } from '../tracking/tracker';

// The two funnel steps the cart and its checkout own: products put in the cart, and an order placed. Both
// carry a value and a currency, which Meta needs to count a conversion; a purchase carries the order's own
// id, total and currency, so the browser's event and the server's Conversions API event are counted once.

/** One product the shopper put in the cart, as the cart's add takes it. */
export type AddedItem = { product: StorefrontProduct; variantId: string; quantity: number };

// Reads the price of one of a product's variants, or 0 when it cannot be priced.
const variantPrice = (product: StorefrontProduct, variantId: string): number => {
  const price = Number(product.variants.find((variant) => variant.id === variantId)?.price ?? 0);
  return Number.isFinite(price) ? price : 0;
};

// Rounds money to the cent, as the platforms show it.
const cents = (value: number): number => Math.round(value * 100) / 100;

// Prices products just added at their variants' own prices: each product, the total, the units, and the name.
export function addedDetail(items: readonly AddedItem[], currency: string): StorefrontEventDetail | null {
  if (items.length === 0) return null;
  const contents = items.map(({ product, variantId, quantity }) => ({ id: product.id, quantity, itemPrice: variantPrice(product, variantId) }));
  const value = contents.reduce((sum, { quantity, itemPrice }) => sum + itemPrice * quantity, 0);
  const numItems = contents.reduce((sum, { quantity }) => sum + quantity, 0);
  return { productId: items[0].product.id, value: cents(value), currency, numItems, contents, contentName: items[0].product.title };
}

// Describes one product page viewed: the product, its price, and its name.
export function viewedDetail(product: StorefrontProduct, currency: string): StorefrontEventDetail {
  const price = variantPrice(product, product.variants[0]?.id ?? '');
  return { productId: product.id, value: price, currency, contentName: product.title, contents: [{ id: product.id, quantity: 1, itemPrice: price }] };
}

// Reads the order the server created into a purchase: its id, number, total, currency and products, or null when it is not an order.
export function placedDetail(raw: unknown, contents: StorefrontEventContent[], currency: string): StorefrontEventDetail | null {
  if (!raw || typeof raw !== 'object') return null;
  const order = raw as Record<string, unknown>;
  const value = Number(order.totalPrice);
  if (typeof order.id !== 'string' || !Number.isFinite(value)) return null;
  const own = typeof order.currency === 'string' && order.currency !== '' ? order.currency : currency;
  const orderNumber = typeof order.orderNumber === 'string' ? order.orderNumber : undefined;
  const numItems = contents.reduce((sum, { quantity }) => sum + quantity, 0);
  return { orderId: order.id, orderNumber, value, currency: own, numItems, contents };
}

// Fires AddToCart for products just put in the cart.
export function trackAdded(items: readonly AddedItem[], currency: string): void {
  const detail = addedDetail(items, currency);
  if (detail) trackEvent('add_to_cart', detail);
}

// Fires Purchase for an order the server just created.
export function trackPlaced(raw: unknown, contents: StorefrontEventContent[], currency: string): void {
  const detail = placedDetail(raw, contents, currency);
  if (detail) trackEvent('purchase', { ...detail, productId: contents[0]?.id });
}
